import { useCallback, useEffect, useContext } from "react"

import { AuthContext } from "./store"
import { CheckoutContext } from "../checkout/store"
import { NotificationContext } from "../notifications/context"

import {
  LOGIN_STATUS_SESSION_KEY,
  CSRF_TOKEN_SESSION_KEY,
  LOGIN_RETURN_URI_SESSION_KEY,
  QUERY_STRING_SESSION_KEY,
} from "@constants/V2/auth"
import {
  sessionStorageAvailable,
  shop,
  validateBusinessUUID,
} from "@utils/shop"
import {
  fetchQueryString,
  filterOutQueryString,
  parseQueryString,
} from "@utils/url"

const AUTH_CHANGE_EVENT = "authStatusChanged"

export const useAuth = () => {
  const checkoutContext = useContext(CheckoutContext)
  const notificationContext = useContext(NotificationContext)
  const authContext = useContext(AuthContext)
  const laxSession = process.env.GATSBY_LAX_SESSION === "true"

  const signOut = useCallback(() => {
    sessionStorage.removeItem(LOGIN_STATUS_SESSION_KEY)
    sessionStorage.removeItem(CSRF_TOKEN_SESSION_KEY)
    sessionStorage.removeItem(LOGIN_RETURN_URI_SESSION_KEY)

    const filteredQueryString = filterOutQueryString(fetchQueryString() ?? "", [
      "businessUUID",
    ])

    if (filteredQueryString) {
      sessionStorage.setItem(QUERY_STRING_SESSION_KEY, filteredQueryString)
    } else {
      sessionStorage.removeItem(QUERY_STRING_SESSION_KEY)
    }

    authContext.setIsAuthenticated(false)
    authContext.setBusinessId(null)
    globalThis.dispatchEvent(new Event(AUTH_CHANGE_EVENT))
  }, [authContext])

  useEffect(() => {
    const validateAuth = async () => {
      if (!sessionStorageAvailable()) return

      const queryBusinessId = parseQueryString(
        fetchQueryString() ?? ""
      ).businessUUID
      const checkAuth = sessionStorage.getItem(LOGIN_STATUS_SESSION_KEY)
      if (checkAuth) {
        const csrfToken = sessionStorage.getItem(CSRF_TOKEN_SESSION_KEY)

        if (!csrfToken) {
          signOut()
          return
        }

        try {
          const { default_business_id } = await shop.oauth.getMe(
            csrfToken,
            laxSession
          )

          if (default_business_id) {
            authContext.setIsAuthenticated(true)
            authContext.setBusinessId(default_business_id)
          } else {
            signOut()
          }
        } catch (error) {
          console.error(`Failed to validate auth: ${error}`)
          signOut()
        }
      } else if (validateBusinessUUID(queryBusinessId)) {
        authContext.setIsAuthenticated(true)
        authContext.setBusinessId(queryBusinessId)
      } else {
        authContext.setIsAuthenticated(false)
      }
    }

    validateAuth()

    globalThis.addEventListener(AUTH_CHANGE_EVENT, validateAuth)
    return () => globalThis.removeEventListener(AUTH_CHANGE_EVENT, validateAuth)
  }, [authContext, laxSession, signOut])

  const handleLogin = useCallback(async () => {
    checkoutContext.setIsHandlingLogin(true)

    try {
      if (sessionStorageAvailable()) {
        const session = await shop.oauth.startSession(laxSession)
        sessionStorage.setItem(CSRF_TOKEN_SESSION_KEY, session.csrf_token)
        sessionStorage.setItem(
          LOGIN_RETURN_URI_SESSION_KEY,
          globalThis.location.pathname
        )

        const redirectUrl = await shop.oauth.getAuthUrl(
          session.csrf_token,
          laxSession
        )
        globalThis.location.href = redirectUrl
      }
      // We leave `isHandlingLogin` state `true` here since the page will redirect
    } catch (error) {
      console.error(`Failed to handleLogin....${error}`)
      setTimeout(
        () =>
          notificationContext.setNotification({
            type: "danger",
            title: "Failed to login",
            message: `Oops! Please try again later.`,
          }),
        500
      )

      checkoutContext.setIsHandlingLogin(false)
    }
  }, [checkoutContext, notificationContext, laxSession])

  return {
    isAuthenticated: authContext.isAuthenticated,
    businessId: authContext.businessId,
    signOut,
    handleLogin,
  }
}
